import { clsx } from 'clsx';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import {
  ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
  useControls,
} from 'react-zoom-pan-pinch';
import { LoadingIndicator } from '@shared/components/loading';
import { API } from '@shared/types/api';
import { FileUpload } from '@shared/types/fieldValues';
import { useElementSize, useImageSrcAndDimensions } from '@shared/util/hooks';
import classNames from './ZoomableImage.module.css';

// Double click never zooms in less than this, e.g. for an image that already fills the container
const MIN_DOUBLE_CLICK_SCALE = 2;
// Matches the library's default maxScale
const MAX_SCALE = 8;

export interface ZoomableImageProps {
  image: FileUpload | API.ChatAttachmentFragment;
  shouldHaveHorizontalMargins?: boolean;
  onZoomStart?: VoidFunction;
  onZoomEnd?: VoidFunction;
}

/*
    Simple wrapper around the image in the preview. The reason we split this into
    separate files for native and web is because of the native specific things
    happening in content.native.tsx
*/
export function ZoomableImage({
  image,
  shouldHaveHorizontalMargins,
  onZoomStart,
  onZoomEnd,
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ReactZoomPanPinchContentRef | null>(null);
  const containerDims = useElementSize(containerRef as MutableRefObject<HTMLElement>);
  const [isZoomed, setIsZoomed] = useState(false);
  const prevIsZoomedRef = useRef(false);

  const { src, width, height, resolved } = useImageSrcAndDimensions({
    image,
    containerWidth: containerDims[0],
    containerHeight: containerDims[1],
  });

  // Double click zooms until the image fills the container on its narrower axis,
  // e.g. a tall screenshot zooms to the full container width
  const fillScale =
    width && height ? Math.max(containerDims[0] / width, containerDims[1] / height) : 1;
  const doubleClickScale = Math.min(Math.max(fillScale, MIN_DOUBLE_CLICK_SCALE), MAX_SCALE);

  // Notify parent when zoomed state transitions so it can show/hide nav controls.
  useEffect(() => {
    if (isZoomed === prevIsZoomedRef.current) {
      return;
    }
    prevIsZoomedRef.current = isZoomed;
    if (isZoomed) {
      onZoomStart?.();
    } else {
      onZoomEnd?.();
    }
  }, [isZoomed, onZoomStart, onZoomEnd]);

  return (
    <>
      <div
        ref={containerRef}
        className={clsx(
          classNames.zoomableImage,
          shouldHaveHorizontalMargins && classNames.horizontalMargins,
        )}
        // stop a double click from selecting the image and the modal title
        onMouseDown={event => {
          if (event.detail > 1) {
            event.preventDefault();
          }
        }}
      >
        <TransformWrapper
          ref={zoomRef}
          // keep the image on screen while zooming and panning: centered on any axis
          // where it fits, and no empty space past an edge where it doesn't.
          // disablePadding clamps during the gesture instead of snapping back after release
          centerZoomedOut
          disablePadding
          maxScale={MAX_SCALE}
          // don't allow panning when not zoomed,
          // so carousel swipe will work properly
          panning={{ disabled: !isZoomed }}
          // two-finger trackpad scroll and the mouse wheel pan;
          // pinch and ctrl + wheel zoom
          wheel={{ wheelDisabled: true }}
          trackPadPanning={{ disabled: !isZoomed }}
          // double click / double tap zooms in on that point, or back out when zoomed.
          // step is added to the current scale of 1
          doubleClick={{ mode: isZoomed ? 'reset' : 'zoomIn', step: doubleClickScale - 1 }}
          // onTransform also fires when the reset animation lands, unlike onZoom
          onTransform={(_ref, state) => {
            setIsZoomed(state.scale > 1);
          }}
        >
          <ZoomableImageContent
            src={src}
            alt={image.name}
            width={width}
            height={height}
            resolved={resolved}
            containerDims={containerDims}
            isZoomed={isZoomed}
          />
        </TransformWrapper>
      </div>
    </>
  );
}

interface ZoomableImageContentProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  // true once the browser has loaded the image and confirmed its natural dimensions;
  // until then the image stays transparent and a loading indicator shows in its place
  resolved: boolean;
  containerDims: number[];
  isZoomed: boolean;
}

function ZoomableImageContent({
  src,
  alt,
  width,
  height,
  resolved,
  containerDims,
  isZoomed,
}: ZoomableImageContentProps) {
  const { setTransform } = useControls();

  useEffect(() => {
    // Don't reposition while the user is zoomed in — dimension changes (e.g. the
    // post-EXIF orientation correction landing) must not reset the transform and snap the view back to scale=1.
    // When isZoomed transitions back to false, this effect re-runs and re-centers.
    if (isZoomed) {
      return;
    }
    const x = (containerDims[0] - width) / 2;
    const y = (containerDims[1] - height) / 2;

    setTransform(x, y, 1, 0);
  }, [setTransform, containerDims, width, height, isZoomed]);

  return (
    <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
      <img
        src={src}
        style={{
          width,
          height,
          opacity: resolved ? 1 : 0,
          transition: 'opacity 150ms ease-in',
        }}
        alt={alt}
      />
      {!resolved && (
        <div className={classNames.loading}>
          <LoadingIndicator fill center padding={4} />
        </div>
      )}
    </TransformComponent>
  );
}
